import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import Button from 'layout/atoms/Button';
import Tooltip from 'layout/atoms/Tooltip';
import {
    selectLayout,
    setSearchQuery as setSearchQueryAction,
} from 'store/layoutSlice';

import styles from './FloatingSearchButton.module.scss';

const FloatingSearchButton = () => {
    const dispatch = useDispatch();
    const layoutState = useSelector(selectLayout);
    const [isExpanded, setIsExpanded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleSearchClick = () => {
        if (isExpanded) {
            // Toggle: close if already expanded
            setIsExpanded(false);
            dispatch(setSearchQueryAction(''));
        } else {
            // Open if collapsed
            setIsExpanded(true);
        }
    };

    // Expose the click handler for keyboard shortcuts
    useEffect(() => {
        if (wrapperRef.current) {
            (wrapperRef.current as any).triggerSearch = handleSearchClick;
        }
    }, [isExpanded]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Search is handled by Redux state change
    };

    const handleClose = () => {
        setIsExpanded(false);
        dispatch(setSearchQueryAction(''));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSearchQueryAction(e.target.value));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            // Blur the input on ESC
            inputRef.current?.blur();
        }
    };

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    return (
        <div
            ref={wrapperRef}
            className={`${styles.wrap} ${isExpanded ? styles.expanded : ''}`}
            data-search-wrapper="true"
        >
            {!isExpanded ? (
                <Tooltip content="Search (Q)">
                    <Button
                        size="m"
                        theme="primary"
                        shape="square"
                        onClick={handleSearchClick}
                    >
                        <SearchIcon />
                    </Button>
                </Tooltip>
            ) : (
                <form
                    onSubmit={handleSearchSubmit}
                    className={styles.searchForm}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        value={layoutState.searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search..."
                        className={styles.searchInput}
                    />
                    <button
                        type="button"
                        onClick={handleClose}
                        className={styles.closeButton}
                    >
                        <CloseIcon />
                    </button>
                </form>
            )}
        </div>
    );
};

export default FloatingSearchButton;